import { ROLES, usuarioActualizarSchema, type UsuarioActualizarInput, type UsuarioPublic } from '@app/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { usuariosApi } from '../api/usuarios.api';

interface Props {
  usuario: UsuarioPublic;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UsuarioEditarDialog({ usuario, open, onOpenChange }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [globalError, setGlobalError] = useState<string | null>(null);
  const es = useI18n();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UsuarioActualizarInput>({
    resolver: zodResolver(usuarioActualizarSchema),
    defaultValues: {
      nombre: usuario.nombre,
      rol: usuario.rol,
      activo: usuario.activo,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: UsuarioActualizarInput) => usuariosApi.update(usuario.id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.usuarios.all });
      toast.success(es.admin.usuarios.editar_dialog.exito);
      onOpenChange(false);
    },
    onError: (error) => {
      setGlobalError(getErrorMessage(error, es.errores.generico.algoSalioMal));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{es.admin.usuarios.editar_dialog.titulo}</DialogTitle>
          <DialogDescription>{usuario.email}</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => void handleSubmit((d) => mutation.mutate(d))(e)}
          className="flex flex-col gap-4"
          noValidate
        >
          {globalError && <Alert variant="error">{globalError}</Alert>}

          <FormField
            label={es.admin.usuarios.editar_dialog.nombre}
            htmlFor="nombre"
            error={errors.nombre?.message}
          >
            <Input id="nombre" aria-invalid={!!errors.nombre} {...register('nombre')} />
          </FormField>

          <FormField label={es.admin.usuarios.editar_dialog.rol} htmlFor="rol" error={errors.rol?.message}>
            <Controller
              control={control}
              name="rol"
              render={({ field }) => (
                <Select value={field.value ?? usuario.rol} onValueChange={field.onChange}>
                  <SelectTrigger id="rol">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((rol) => (
                      <SelectItem key={rol} value={rol}>
                        {es.roles[rol]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>

          <FormField label={es.admin.usuarios.editar_dialog.activo} htmlFor="activo">
            <Controller
              control={control}
              name="activo"
              render={({ field }) => (
                <Select
                  value={String(field.value ?? true)}
                  onValueChange={(v) => field.onChange(v === 'true')}
                >
                  <SelectTrigger id="activo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">{es.admin.usuarios.estado.activo}</SelectItem>
                    <SelectItem value="false">{es.admin.usuarios.estado.inactivo}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {es.admin.usuarios.editar_dialog.cancelar}
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending
                ? es.admin.usuarios.editar_dialog.guardando
                : es.admin.usuarios.editar_dialog.guardar}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
